export type DemoLocale = 'es' | 'en'

export type LocalizedCopy = Record<DemoLocale, string>

export type DemoRendition = {
    hevc: string
    webm: string
    poster: string
    width: number
    height: number
}

export type DemoChapter = {
    startSeconds: number
    endSeconds: number
    title: LocalizedCopy
    body: LocalizedCopy
}

export type LandingDemoId = 'product-tour' | 'escandallo-voz'

export type LandingDemo = {
    id: LandingDemoId
    durationSeconds: number
    uploadDate: string
    captionsPath: string
    portrait: DemoRendition
    landscape?: DemoRendition
    name: LocalizedCopy
    description: LocalizedCopy
    chapters: readonly DemoChapter[]
}

const PRODUCT_TOUR_CHAPTERS = [
    {
        startSeconds: 0,
        endSeconds: 15,
        title: {
            es: 'Recetario',
            en: 'Recipe book',
        },
        body: {
            es: 'Pantalla Recetario con food cost global 29% y el coste y PVP de cada plato (patatas bravas, ensaladilla, croquetas).',
            en: 'Recipe book screen with 29% global food cost and each dish cost and menu price (patatas bravas, Russian salad, croquettes).',
        },
    },
    {
        startSeconds: 15,
        endSeconds: 35,
        title: {
            es: 'Albaranes',
            en: 'Delivery notes',
        },
        body: {
            es: '«Solo hazle una foto.» Tipi lee el albarán (tomate, cebolla, pimiento, limón) y muestra cantidades, precios e IVA; total 2.095,10 €.',
            en: '“Just take a photo.” Tipi reads the delivery note (tomato, onion, pepper, lemon) with quantities, prices and VAT; total €2,095.10.',
        },
    },
    {
        startSeconds: 35,
        endSeconds: 60,
        title: {
            es: 'Proveedores',
            en: 'Suppliers',
        },
        body: {
            es: 'Gasto de proveedores en agosto: 18.025,94 €, 6 albaranes. Objetivo 28% food cost (hace falta facturar 64.400 €). Listado de proveedores con pagado o pendiente.',
            en: 'August supplier spend: €18,025.94, 6 delivery notes. 28% food-cost target (need to invoice €64,400). Supplier list with paid or pending.',
        },
    },
    {
        startSeconds: 60,
        endSeconds: 85,
        title: {
            es: 'Inventario',
            en: 'Inventory',
        },
        body: {
            es: 'Inventario de fin de mes: teórico vs real (solomillo, pulpo, parmesano, gambón), descuadres en euros y botón para confirmar el inventario.',
            en: 'Month-end inventory: theoretical vs actual (beef tenderloin, octopus, parmesan, prawns), euro variances, and confirm inventory.',
        },
    },
    {
        startSeconds: 85,
        endSeconds: 93,
        title: {
            es: 'Cierre',
            en: 'End card',
        },
        body: {
            es: 'Tipi: toda la gestión sin ordenador; escandallos por voz; ingredientes con los nombres de tu día a día; controla cada gasto del negocio.',
            en: 'Tipi: run the whole operation without a computer; voice recipe costing; ingredients named the way you talk in the kitchen; control every business expense.',
        },
    },
] as const satisfies readonly DemoChapter[]

const ESCANDALLO_VOZ_CHAPTERS = [
    {
        startSeconds: 0,
        endSeconds: 5,
        title: {
            es: 'Cocina y recetario',
            en: 'Kitchen and recipe book',
        },
        body: {
            es: 'Un jefe de cocina en el móvil. Recetario con food cost global 29% y el micrófono flotante listo para dictar.',
            en: 'A head chef on their phone. Recipe book at 29% global food cost and the floating mic ready to dictate.',
        },
    },
    {
        startSeconds: 5,
        endSeconds: 11,
        title: {
            es: 'Dictado',
            en: 'Dictation',
        },
        body: {
            es: 'Se crea la receta de croquetas por voz: leche, harina, mantequilla y jamón, 120 raciones, con coste de cada línea. Tipi marca «Receta creada».',
            en: 'A croquette recipe is created by voice: milk, flour, butter and ham, 120 portions, with each line cost. Tipi marks “Recipe created”.',
        },
    },
    {
        startSeconds: 11,
        endSeconds: 12,
        title: {
            es: 'Cierre',
            en: 'End card',
        },
        body: {
            es: 'Hablas. Tipi calcula.',
            en: 'You speak. Tipi calculates.',
        },
    },
] as const satisfies readonly DemoChapter[]

const DEMO_STORAGE_BASE =
    'https://firebasestorage.googleapis.com/v0/b/tipi-ink.appspot.com/o/public_sales_materials%2Fdemos%2F'

function demoStorageUrl(fileName: string, token: string) {
    return `${DEMO_STORAGE_BASE}${fileName}?alt=media&token=${token}`
}

export const LANDING_DEMOS: Record<LandingDemoId, LandingDemo> = {
    'product-tour': {
        id: 'product-tour',
        durationSeconds: 93,
        uploadDate: '2026-08-18',
        captionsPath: '/demos/product-tour.es.vtt',
        portrait: {
            hevc: demoStorageUrl('product-tour-portrait.mp4', '6e8b7936-7ad3-4449-b7dc-346a1c33963e'),
            webm: demoStorageUrl('product-tour-portrait.webm', '054e1ad8-cf70-4623-a2a6-b526ea121e96'),
            poster: '/demos/product-tour-portrait-poster.webp',
            width: 720,
            height: 1280,
        },
        landscape: {
            hevc: demoStorageUrl('product-tour-landscape.mp4', 'f300abc5-e010-473e-a6a7-eeeed5684db2'),
            webm: demoStorageUrl('product-tour-landscape.webm', '52c778a1-8b16-4bcf-8d0a-28dac3d1d9ea'),
            poster: '/demos/product-tour-landscape-poster.webp',
            width: 1920,
            height: 1080,
        },
        name: {
            es: 'Recorrido de Tipi: recetario, albaranes, proveedores e inventario',
            en: 'Tipi walkthrough: recipe book, delivery notes, suppliers and inventory',
        },
        description: {
            es: 'Demo de 93 segundos de Tipi en el móvil: ves el coste de cada plato, subes el albarán con una foto, controlas el gasto de cada proveedor y cierras el inventario de fin de mes. Sin ordenador.',
            en: 'A 93-second Tipi demo on a phone: see each dish cost, photograph a delivery note, track supplier spend, and close month-end inventory. No computer.',
        },
        chapters: PRODUCT_TOUR_CHAPTERS,
    },
    'escandallo-voz': {
        id: 'escandallo-voz',
        durationSeconds: 12,
        uploadDate: '2026-08-18',
        captionsPath: '/demos/escandallo-voz.es.vtt',
        portrait: {
            hevc: demoStorageUrl('escandallo-voz-portrait.mp4', '47399b99-bb89-4f40-8431-cb03ec896b7b'),
            webm: demoStorageUrl('escandallo-voz-portrait.webm', '2143e21c-2e19-4290-b303-59e76c1c5411'),
            poster: '/demos/escandallo-voz-portrait-poster.webp',
            width: 720,
            height: 1280,
        },
        name: {
            es: 'Escandallo por voz: croquetas de jamón en 12 segundos',
            en: 'Voice recipe costing: ham croquettes in 12 seconds',
        },
        description: {
            es: 'Un cocinero dicta la receta de croquetas. Tipi calcula el coste por ración y marca la receta como creada. Hablas. Tipi calcula.',
            en: 'A cook dictates a croquette recipe. Tipi calculates cost per portion and marks the recipe created. You speak. Tipi calculates.',
        },
        chapters: ESCANDALLO_VOZ_CHAPTERS,
    },
}

export function iso8601Duration(seconds: number): string {
    const whole = Math.round(seconds)
    const minutes = Math.floor(whole / 60)
    const remainder = whole % 60
    if (minutes === 0) return `PT${remainder}S`
    if (remainder === 0) return `PT${minutes}M`
    return `PT${minutes}M${remainder}S`
}

export function demoTranscript(demo: LandingDemo, locale: DemoLocale): string {
    return demo.chapters.map(chapter => `${chapter.title[locale]}. ${chapter.body[locale]}`).join(' ')
}

export function hevcSourceType(): string {
    return 'video/mp4; codecs="hvc1"'
}

export function webmSourceType(): string {
    return 'video/webm'
}

export function demoVideoSources(rendition: DemoRendition): Array<{ src: string; type: string }> {
    return [
        { src: rendition.hevc, type: hevcSourceType() },
        { src: rendition.webm, type: webmSourceType() },
    ]
}
